import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Camera, Play } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { SectionHead } from "@/components/site/InfoSections";
import { PlayOverlay, SampleBadge, StatusBadge } from "@/components/site/Badges";

const FILTERS = ["All", "In Progress", "Completed"];

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const CountBadge = ({ photos, videos }) => {
  if (photos + videos < 2) return null;
  const label = [photos > 0 && plural(photos, "photo"), videos > 0 && plural(videos, "video")].filter(Boolean).join(", ");
  return (
    <span
      className="absolute bottom-3 right-3 inline-flex items-center gap-2.5 rounded-full bg-black/65 px-3 py-1.5 text-sm font-semibold text-white ring-1 ring-white/15 backdrop-blur-sm"
      aria-label={label}
      role="img"
      data-testid="project-card-count-badge"
    >
      {photos > 0 && <span className="inline-flex items-center gap-1" data-testid="project-card-photo-count"><Camera className="h-4 w-4" aria-hidden="true" />{photos}</span>}
      {videos > 0 && <span className="inline-flex items-center gap-1" data-testid="project-card-video-count"><Play className="h-3.5 w-3.5 fill-current" aria-hidden="true" />{videos}</span>}
    </span>
  );
};

const ProjectCard = ({ p, i }) => (
  <Link
    to={`/work/${p.id}`}
    className="group flex h-full flex-col overflow-hidden rounded-md border border-zinc-800 bg-[#1A1A1E] transition-colors duration-200 hover:border-[#FFD23F]/60"
    data-testid={`project-card-${p.id}`}
    aria-label={p.title || "Project photos"}
  >
    <div className={`relative aspect-[4/3] overflow-hidden bg-[#0d0d0f] ${p.title ? "" : "grow"}`}>
      {p.cover?.url ? (
        <img
          src={mediaUrl(p.cover.url)}
          alt={p.title || (p.cover.kind === "video" ? "Project video" : "Project photo")}
          loading={i < 3 ? "eager" : "lazy"}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          data-testid="project-card-cover"
        />
      ) : (
        <div className="grid h-full place-items-center text-zinc-600"><Camera className="h-10 w-10" /></div>
      )}
      {p.cover?.kind === "video" && <PlayOverlay small />}
      <div className="absolute left-3 top-3 flex gap-2">
        <StatusBadge status={p.status} testId="project-card-status" />
        {p.is_sample && <SampleBadge testId="project-card-sample-badge" />}
      </div>
      <CountBadge photos={p.photo_count} videos={p.video_count} />
    </div>
    {p.title && <div className="flex items-start justify-between gap-3 p-5">
      <div className="min-w-0">
        <h3 className="font-sub text-lg font-semibold uppercase tracking-wide text-[#FAF7F2]" data-testid="project-card-title">{p.title}</h3>
      </div>
      <ArrowUpRight className="h-5 w-5 shrink-0 text-zinc-500 transition-colors group-hover:text-[#FFD23F]" />
    </div>}
  </Link>
);

export const OurWork = ({ projects }) => {
  const [filter, setFilter] = useState("All");
  const shown = projects.filter((p) => filter === "All" || p.status === filter);
  return (
    <section id="our-work" className="section-anchor dot-grid relative bg-[#121214]" data-testid="our-work-section">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24">
        <SectionHead eyebrow="Fresh from the booth" title="Our Work" />
        {projects.length === 0 ? (
          <p className="rounded-md border border-dashed border-zinc-700 bg-[#1A1A1E] p-10 text-center text-lg text-zinc-300" data-testid="our-work-empty">
            New project photos coming soon.
          </p>
        ) : (
          <>
            <div className="mb-8 flex flex-wrap gap-2" role="tablist">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  role="tab"
                  aria-selected={filter === f}
                  onClick={() => setFilter(f)}
                  className={`font-sub h-11 rounded-full px-5 text-sm font-semibold uppercase tracking-wider transition-colors ${
                    filter === f ? "bg-[#FFD23F] text-[#121214]" : "border border-zinc-700 text-zinc-300 hover:border-[#FFD23F] hover:text-[#FFD23F]"
                  }`}
                  data-testid={`work-filter-${f.toLowerCase().replace(" ", "-")}`}
                >
                  {f}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="project-grid">
              {shown.map((p, i) => <ProjectCard key={p.id} p={p} i={i} />)}
            </div>
            {shown.length === 0 && <p className="text-zinc-400" data-testid="our-work-filter-empty">Nothing here yet — check back soon.</p>}
          </>
        )}
      </div>
    </section>
  );
};
